import { medianLength, medianPath } from "./bishun-median.js";

/**
 * One 汉字 as the SVG that draws it, stroke by stroke.
 *
 * Apart from `bishun.ts`, which fetches the outlines and decides when a stroke
 * is written: this is the markup they are written into, and the arithmetic of
 * how far the brush travels along each one.
 *
 * **A stroke is a thick line clipped to its own outline.** The line follows the
 * stroke's median, which is the path a hand takes writing it, and the outline
 * trims it to the shape the stroke actually has. Drawing it is then a
 * `stroke-dashoffset` going to zero under a transition the stylesheet declares,
 * and `modules/cidian-bishun` holds every rule about how it looks.
 */

/**
 * The grid the outlines are drawn on, and the transform that rights them.
 *
 * Make Me a Hanzi's paths use a 1024 by 1024 box whose y axis *decreases*
 * downwards, which is the one trap in the format: rendered without this every
 * character comes out upside down. The 900 is the font's own baseline offset
 * rather than half the box, and it is what the project's own renderer uses.
 */
const gridSize = 1024;
const gridTransform = "scale(1, -1) translate(0, -900)";

/** One character's outlines, as `hanzi-writer-data` publishes them. */
export interface StrokeData {
  /** An SVG path per stroke, in writing order. */
  readonly strokes: readonly string[];

  /** The line each stroke is written along, as points on the same grid. */
  readonly medians: readonly Median[];
}

/** The route a hand takes writing one stroke, as points on the grid. */
export type Median = readonly (readonly [number, number])[];

/**
 * One stroke's brush, and how far it has to travel to write the stroke.
 *
 * The two together rather than an array of each. They are read in step by
 * everything that touches them, and two arrays that have to stay in step are
 * two arrays that can stop being in step.
 */
export interface BrushStroke {
  /** The line that draws the stroke, clipped to the stroke's own outline. */
  readonly brush: SVGElement;

  /** How far along its median the brush travels, in grid units. */
  readonly length: number;
}

/** The SVG namespace, which `createElement` does not imply. */
const svgNamespace = "http://www.w3.org/2000/svg";

/** An SVG element with attributes, since every line below wants both. */
function svg(
  document: Document,
  name: string,
  attributes: Readonly<Record<string, string>>,
): SVGElement {
  const element = document.createElementNS(svgNamespace, name);

  for (const [attribute, value] of Object.entries(attributes)) {
    element.setAttribute(attribute, value);
  }

  return element;
}

/** The whole figure, as one SVG the strokes are drawn into. */
export function strokeDrawing(
  document: Document,
  data: StrokeData,
  strokeParts: string,
): { readonly drawing: SVGElement; readonly strokes: readonly BrushStroke[] } {
  const drawing = svg(document, "svg", {
    viewBox: `0 0 ${String(gridSize)} ${String(gridSize)}`,
    class: "cidian-bishun-svg",
    "aria-hidden": "true",
    focusable: "false",
  });
  const defs = svg(document, "defs", {});
  const grid = svg(document, "g", { transform: gridTransform });
  const guide = svg(document, "g", { class: "cidian-bishun-guide" });
  const ink = svg(document, "g", { class: "cidian-bishun-ink" });

  const strokes = data.strokes.map((stroke, at) => {
    const clipId = `cb-stroke-${String(at)}`;
    const clip = svg(document, "clipPath", { id: clipId });
    const median = medianOf(data, at);
    const brush = svg(document, "path", {
      class: "cidian-bishun-brush",
      d: medianPath(median),
    });
    const clipped = svg(document, "g", {
      "clip-path": `url(#${clipId})`,
      "data-cb-part": strokeParts[at] ?? "",
    });

    clip.append(svg(document, "path", { d: stroke }));
    defs.append(clip);
    guide.append(svg(document, "path", { d: stroke }));
    clipped.append(brush);
    ink.append(clipped);

    return { brush, length: medianLength(median) };
  });

  grid.append(guide, ink);
  drawing.append(defs, grid);

  return { drawing, strokes };
}

/**
 * The median of one stroke, or no line at all where the file has none.
 *
 * The two lists have the same length in every one of the 9,276 characters
 * published, and `stroke-coverage.test.ts` holds them to it. What this is for
 * is a file that arrived truncated, which should cost that stroke its
 * animation rather than the character its figure.
 */
function medianOf(data: StrokeData, at: number): Median {
  return data.medians[at] ?? [];
}
